import type { BattleViewProps } from './BattleViewProps.js';
import { directionArrow } from './actionHelpers.js';
import { cellIndex, getFogAt } from '@tank-battle/shared';
import { FOG_HIDDEN, SIDE_COLORS, TERRAIN_COLORS } from './battleColors.js';

export function BattleGrid({
  map,
  view,
  mySide,
  actionMode,
  canInteract,
  onCellClick,
  canMoveTo,
  canFireAt,
}: BattleViewProps) {
  const { width, height } = map.size;
  const unitsByCell = new Map<string, (typeof view.units)[number]>();
  for (const unit of view.units) {
    if (unit.hp <= 0) continue;
    unitsByCell.set(`${unit.x},${unit.y}`, unit);
  }

  const cells = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const fog = getFogAt(view.fog, x, y);
      const terrain = map.terrain[cellIndex(x, y, width)];
      const unit = unitsByCell.get(`${x},${y}`);
      const moveTarget = canInteract && actionMode === 'move' && canMoveTo(x, y);
      const fireTarget = canInteract && actionMode === 'fire' && canFireAt(x, y);
      const isActive = unit?.id === view.activeUnitId;
      const clickable = moveTarget || fireTarget;

      cells.push(
        <button
          key={`${x}-${y}`}
          type="button"
          className={[
            'battle-cell',
            `fog-${fog}`,
            moveTarget ? 'move-target' : '',
            fireTarget ? 'fire-target' : '',
            isActive ? 'active-unit' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{
            backgroundColor:
              fog === 'hidden'
                ? FOG_HIDDEN
                : TERRAIN_COLORS[terrain?.terrainType ?? 'plain'] ?? '#243447',
            opacity: fog === 'explored' ? 0.55 : 1,
          }}
          disabled={!clickable}
          onClick={() => clickable && onCellClick(x, y)}
          title={`${x},${y}`}
        >
          {unit && fog !== 'hidden' ? (
            <span
              className="unit-marker"
              style={{
                backgroundColor: SIDE_COLORS[unit.side],
                outline: unit.side === mySide ? '2px solid #fff' : '1px solid #000',
              }}
            >
              <span className="hull-arrow">{directionArrow(unit.hullFacing)}</span>
              <span className="turret-dot" title={`Turret ${directionArrow(unit.turretFacing)}`}>
                {directionArrow(unit.turretFacing)}
              </span>
            </span>
          ) : null}
        </button>,
      );
    }
  }

  return (
    <div
      className="battle-grid"
      style={{
        gridTemplateColumns: `repeat(${width}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${height}, minmax(0, 1fr))`,
      }}
    >
      {cells}
    </div>
  );
}
